import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import './App.css';
import { AuthProvider, useAuth, API } from './contexts/AuthContext';
import MixpanelAnalytics, { ANALYTICS_EVENTS } from './analytics/mixpanel';
import { useAnalytics, usePageTracking } from './hooks/useAnalytics';
import { Calendar, MapPin, Users, Plus, LogOut, Settings, QrCode, Share2, X, ChevronRight, Search, Filter, Crown } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import BoxSeatLogo from './components/BoxSeatLogo';

// Pages
import LandingPage from './pages/LandingPage';
import EventsPage from './pages/EventsPage';
import VenuesPage from './pages/VenuesPage';
import PublicMeetupPage from './pages/PublicMeetupPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SeasonPassPage from './pages/SeasonPassPage';
import SeasonPassSuccessPage from './pages/SeasonPassSuccessPage';
import AdminDashboard from './pages/AdminDashboard';

// ============ AUTH COMPONENTS ============

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  usePageTracking('Login');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <BoxSeatLogo size="sm" onClick={() => navigate('/')} />
          </div>
          <p className="text-sm text-zinc-500">Philadelphia Sports & Entertainment</p>
        </div>
        <div className="bg-surface border border-white/10 rounded-xl p-8">
          <h2 className="font-heading text-2xl font-bold uppercase mb-6 text-center">Welcome Back</h2>
          {error && <div data-testid="login-error" className="bg-accent/10 border border-accent/30 text-accent px-4 py-3 rounded-md mb-4 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Password</label>
              <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="w-full py-3 bg-primary text-black font-bold uppercase tracking-wide rounded-md hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber">
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
          <div className="mt-4 text-center">
            <button data-testid="forgot-password-link" onClick={() => navigate('/forgot-password')} className="text-sm text-zinc-500 hover:text-primary transition-colors">
              Forgot Password?
            </button>
          </div>
          <p className="mt-4 text-center text-sm text-zinc-500">
            Don't have an account?{' '}
            <button onClick={() => navigate('/register')} className="text-primary font-semibold hover:underline">Sign Up</button>
          </p>
        </div>
      </div>
    </div>
  );
};

const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await register(email, password, fullName);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <BoxSeatLogo size="sm" onClick={() => navigate('/')} />
          </div>
          <p className="text-sm text-zinc-500">Join the Philly fan community</p>
        </div>
        <div className="bg-surface border border-white/10 rounded-xl p-8">
          <h2 className="font-heading text-2xl font-bold uppercase mb-6 text-center">Create Account</h2>
          {error && <div data-testid="register-error" className="bg-accent/10 border border-accent/30 text-accent px-4 py-3 rounded-md mb-4 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Full Name</label>
              <input data-testid="register-name-input" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Email</label>
              <input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Password</label>
              <input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div>
            <button data-testid="register-submit-btn" type="submit" disabled={loading}
              className="w-full py-3 bg-primary text-black font-bold uppercase tracking-wide rounded-md hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber">
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-zinc-500">
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="text-primary font-semibold hover:underline">Sign In</button>
          </p>
        </div>
      </div>
    </div>
  );
};

// ============ FORGOT / RESET PASSWORD ============

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(`${API}/auth/reset-password-request`, { email });
      setMessage(response.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <BoxSeatLogo size="sm" onClick={() => navigate('/')} />
          </div>
          <p className="text-sm text-zinc-500">Password Recovery</p>
        </div>
        <div className="bg-surface border border-white/10 rounded-xl p-8">
          <h2 className="font-heading text-2xl font-bold uppercase mb-6 text-center">Reset Password</h2>
          {error && <div data-testid="forgot-error" className="bg-accent/10 border border-accent/30 text-accent px-4 py-3 rounded-md mb-4 text-sm">{error}</div>}
          {message && <div data-testid="forgot-success" className="bg-secondary/10 border border-secondary/30 text-secondary px-4 py-3 rounded-md mb-4 text-sm">{message}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Email Address</label>
              <input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" required />
              <p className="text-xs text-zinc-600 mt-2">We'll send you a link to reset your password.</p>
            </div>
            <button data-testid="forgot-submit-btn" type="submit" disabled={loading}
              className="w-full py-3 bg-primary text-black font-bold uppercase tracking-wide rounded-md hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-zinc-500">
            Remember your password?{' '}
            <button onClick={() => navigate('/login')} className="text-primary font-semibold hover:underline">Sign In</button>
          </p>
        </div>
      </div>
    </div>
  );
};

const ResetPasswordForm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError('No reset token provided.');
      return;
    }
    const validate = async () => {
      try {
        const res = await axios.get(`${API}/auth/validate-reset-token/${token}`);
        setTokenValid(res.data.valid);
        if (!res.data.valid) setError('This reset link is invalid or has expired.');
      } catch {
        setTokenValid(false);
        setError('Failed to validate reset token.');
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setMessage(res.data.message + ' You can now sign in with your new password.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <BoxSeatLogo size="sm" onClick={() => navigate('/')} />
          </div>
        </div>
        <div className="bg-surface border border-white/10 rounded-xl p-8">
          <h2 className="font-heading text-2xl font-bold uppercase mb-6 text-center">
            {tokenValid ? 'New Password' : 'Invalid Link'}
          </h2>
          {error && <div data-testid="reset-error" className="bg-accent/10 border border-accent/30 text-accent px-4 py-3 rounded-md mb-4 text-sm">{error}</div>}
          {message && (
            <div className="bg-secondary/10 border border-secondary/30 text-secondary px-4 py-3 rounded-md mb-4 text-sm">
              {message}
              <button onClick={() => navigate('/login')} className="block w-full mt-3 py-2 bg-primary text-black font-bold uppercase text-sm rounded-md hover:bg-primary-hover transition-all">
                Go to Sign In
              </button>
            </div>
          )}
          {tokenValid && !message && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">New Password</label>
                <input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters" required minLength={6}
                  className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Confirm Password</label>
                <input data-testid="reset-confirm-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password" required minLength={6}
                  className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <button data-testid="reset-submit-btn" type="submit" disabled={loading}
                className="w-full py-3 bg-primary text-black font-bold uppercase tracking-wide rounded-md hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber">
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}
          {!tokenValid && (
            <button onClick={() => navigate('/forgot-password')} className="w-full py-3 bg-white/5 border border-white/10 text-white font-bold uppercase rounded-md hover:bg-white/10 transition-all">
              Request New Link
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ DASHBOARD COMPONENTS ============

const MeetupCard = ({ meetup, onJoin, onLeave, onViewDetails }) => {
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const isUpcoming = new Date(meetup.date) > new Date();
  const isFull = meetup.max_participants && meetup.participant_count >= meetup.max_participants;

  return (
    <div data-testid="dashboard-meetup-card" className="bg-surface border border-white/10 rounded-xl p-6 hover:-translate-y-1 transition-transform duration-300">
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-heading text-lg font-bold uppercase line-clamp-2 flex-1">{meetup.title}</h3>
        <span className={`ml-2 px-2 py-1 text-xs rounded-full font-bold uppercase ${meetup.status === 'active' ? 'bg-secondary/10 text-secondary' : 'bg-zinc-800 text-zinc-500'}`}>
          {meetup.status}
        </span>
      </div>
      <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{meetup.description}</p>
      <div className="space-y-2 text-sm text-zinc-500 mb-4">
        <div className="flex items-center gap-2"><Calendar size={14} /> {formatDate(meetup.date)} at {meetup.time}</div>
        <div className="flex items-center gap-2"><MapPin size={14} /> <span className="line-clamp-1">{meetup.location}</span></div>
        <div className="flex items-center gap-2"><Users size={14} /> {meetup.participant_count}{meetup.max_participants ? ` / ${meetup.max_participants}` : ''} attending {isFull && <span className="text-accent font-bold">FULL</span>}</div>
      </div>
      {meetup.categories?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {meetup.categories.map((c) => <span key={c} className="px-2 py-1 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full">{c}</span>)}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => onViewDetails(meetup)} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm font-bold uppercase rounded-md hover:bg-white/10 transition-all">View</button>
        {isUpcoming && meetup.status === 'active' && !meetup.is_creator && (
          meetup.is_participant ? (
            <button onClick={() => onLeave(meetup.id)} className="px-3 py-2 bg-accent/10 text-accent text-sm font-bold uppercase rounded-md hover:bg-accent/20 transition-all">Leave</button>
          ) : (
            <button onClick={() => onJoin(meetup.id)} disabled={isFull} className="px-3 py-2 bg-primary text-black text-sm font-bold uppercase rounded-md hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber">Join</button>
          )
        )}
        {meetup.is_creator && <span className="px-3 py-2 bg-primary/10 text-primary text-xs font-bold uppercase rounded-md flex items-center">Creator</span>}
      </div>
    </div>
  );
};

const CreateMeetupForm = ({ onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categoryOptions = ['Tailgate', 'Watch Party', 'Pre-Game', 'Post-Game', 'Fan March', 'Concert', 'General'];
  const tagOptions = ['Eagles', 'Phillies', 'Sixers', 'Flyers', 'Union', 'BBQ', 'Nightlife', 'Family', 'Live Music'];

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/meetups`, {
        title, description,
        date: new Date(`${date}T${time}`).toISOString(),
        time, location,
        max_participants: maxParticipants ? parseInt(maxParticipants) : null,
        categories, tags,
      });
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create meetup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-surface border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl shadow-amber-500/10">
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold uppercase">Create Meetup</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"><X size={16} /></button>
          </div>
          {error && <div className="bg-accent/10 border border-accent/30 text-accent px-4 py-3 rounded-md mb-4 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Title *</label>
              <input data-testid="create-meetup-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Pre-Game Tailgate" required maxLength={200}
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Description *</label>
              <textarea data-testid="create-meetup-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} required placeholder="What's the plan?"
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Date *</label>
                <input data-testid="create-meetup-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} min={minDate} required
                  className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Time *</label>
                <input data-testid="create-meetup-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} required
                  className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Location *</label>
              <input data-testid="create-meetup-location" type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g., Citizens Bank Park - Lot K" required maxLength={300}
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Max Participants</label>
              <input type="number" value={maxParticipants} onChange={(e) => setMaxParticipants(e.target.value)} min="1" max="1000" placeholder="Unlimited"
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Categories</label>
              <div className="flex flex-wrap gap-2">
                {categoryOptions.map(c => (
                  <button key={c} type="button" onClick={() => setCategories(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c].slice(0, 5))}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${categories.includes(c) ? 'bg-primary text-black border-primary font-bold' : 'bg-white/5 text-zinc-400 border-white/10 hover:bg-white/10'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Tags</label>
              <div className="flex flex-wrap gap-2">
                {tagOptions.map(t => (
                  <button key={t} type="button" onClick={() => setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].slice(0, 10))}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${tags.includes(t) ? 'bg-secondary text-black border-secondary font-bold' : 'bg-white/5 text-zinc-400 border-white/10 hover:bg-white/10'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button data-testid="create-meetup-submit" type="submit" disabled={loading}
                className="flex-1 py-3 bg-primary text-black font-bold uppercase tracking-wide rounded-md hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber">
                {loading ? 'Creating...' : 'Create Meetup'}
              </button>
              <button type="button" onClick={onClose} className="px-6 py-3 bg-white/5 text-white font-bold uppercase rounded-md hover:bg-white/10 transition-all">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const MeetupDetailsModal = ({ meetup, onClose, onJoin, onLeave }) => {
  const [promoting, setPromoting] = useState(false);
  const [shareData, setShareData] = useState(null);
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const isUpcoming = new Date(meetup.date) > new Date();
  const isFull = meetup.max_participants && meetup.participant_count >= meetup.max_participants;

  const handlePromote = async () => {
    setPromoting(true);
    try {
      const res = await axios.post(`${API}/meetups/${meetup.id}/promote`);
      setShareData(res.data);
    } catch (err) {
      console.error('Failed to promote:', err);
    } finally {
      setPromoting(false);
    }
  };

  const shareUrl = shareData ? `${window.location.origin}/m/${shareData.share_id}` : (meetup.share_id ? `${window.location.origin}/m/${meetup.share_id}` : null);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-surface border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl shadow-amber-500/10">
        <div className="h-1 bg-gradient-to-r from-primary via-primary to-transparent" />
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="font-heading text-3xl font-black tracking-tight uppercase mb-2">{meetup.title}</h1>
              <span className={`px-3 py-1 text-xs rounded-full font-bold uppercase ${meetup.status === 'active' ? 'bg-secondary/10 text-secondary' : 'bg-zinc-800 text-zinc-500'}`}>
                {meetup.status}
              </span>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"><X size={16} /></button>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"><Calendar size={18} className="text-primary" /></div>
                  <div><p className="font-semibold">{formatDate(meetup.date)}</p><p className="text-sm text-zinc-500">at {meetup.time}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"><MapPin size={18} className="text-primary" /></div>
                  <p className="font-semibold">{meetup.location}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"><Users size={18} className="text-primary" /></div>
                  <p className="font-semibold">{meetup.participant_count}{meetup.max_participants ? ` / ${meetup.max_participants}` : ''} attending</p>
                </div>
              </div>
              <h3 className="font-heading text-lg font-bold uppercase mb-3">About</h3>
              <p className="text-zinc-400 whitespace-pre-wrap mb-6">{meetup.description}</p>
              {meetup.categories?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {meetup.categories.map((c) => <span key={c} className="px-3 py-1 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full font-semibold">{c}</span>)}
                </div>
              )}
              {meetup.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {meetup.tags.map((t) => <span key={t} className="text-xs text-zinc-600">#{t}</span>)}
                </div>
              )}
            </div>

            <div className="space-y-4">
              {isUpcoming && meetup.status === 'active' && !meetup.is_creator && (
                meetup.is_participant ? (
                  <button onClick={() => { onLeave(meetup.id); onClose(); }} className="w-full py-3 bg-accent/10 text-accent font-bold uppercase rounded-md hover:bg-accent/20 transition-all">Leave Meetup</button>
                ) : (
                  <button onClick={() => { onJoin(meetup.id); onClose(); }} disabled={isFull}
                    className="w-full py-3 bg-primary text-black font-bold uppercase rounded-md hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber">
                    {isFull ? 'Meetup Full' : 'Join Meetup'}
                  </button>
                )
              )}
              {meetup.is_creator && (
                <>
                  <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg text-center text-primary text-sm font-bold uppercase">You Created This</div>
                  <button onClick={handlePromote} disabled={promoting}
                    className="w-full py-3 bg-white/5 border border-white/10 text-white font-bold uppercase text-sm rounded-md hover:bg-white/10 transition-all flex items-center justify-center gap-2">
                    <QrCode size={16} /> {promoting ? 'Generating...' : 'Get Share Link'}
                  </button>
                </>
              )}
              {(shareUrl || meetup.share_id) && (
                <div className="bg-black/30 border border-white/10 rounded-lg p-4 text-center">
                  <div className="bg-white p-3 rounded-lg inline-block mb-3">
                    <QRCodeSVG value={shareUrl || `${window.location.origin}/m/${meetup.share_id}`} size={120} />
                  </div>
                  <p className="text-xs text-zinc-500 mb-2">Share this meetup</p>
                  <button onClick={() => { navigator.clipboard.writeText(shareUrl || `${window.location.origin}/m/${meetup.share_id}`); }}
                    className="w-full py-2 bg-white/5 border border-white/10 text-white text-xs font-bold uppercase rounded-md hover:bg-white/10 transition-all flex items-center justify-center gap-1">
                    <Share2 size={12} /> Copy Link
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ DASHBOARD ============

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('meetups');
  const [meetups, setMeetups] = useState([]);
  const [myMeetups, setMyMeetups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedMeetup, setSelectedMeetup] = useState(null);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  usePageTracking('Dashboard');

  useEffect(() => {
    if (activeTab === 'meetups') fetchMeetups();
    if (activeTab === 'my-meetups') fetchMyMeetups();
  }, [activeTab]);

  const fetchMeetups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/meetups?status=active`);
      setMeetups(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchMyMeetups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my-meetups`);
      setMyMeetups(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleJoin = async (id) => {
    try {
      await axios.post(`${API}/meetups/${id}/join`);
      fetchMeetups();
      fetchMyMeetups();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to join'); }
  };

  const handleLeave = async (id) => {
    try {
      await axios.post(`${API}/meetups/${id}/leave`);
      fetchMeetups();
      fetchMyMeetups();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to leave'); }
  };

  const tabs = [
    { id: 'meetups', label: 'Browse Meetups' },
    { id: 'my-meetups', label: 'My Meetups' },
  ];

  const currentMeetups = activeTab === 'meetups' ? meetups : myMeetups;

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Header */}
      <header data-testid="dashboard-header" className="sticky top-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <BoxSeatLogo size="xs" onClick={() => navigate('/')} />
            <nav className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/venues')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase">Venues</button>
              <button onClick={() => navigate('/events')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase">Events</button>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold">{user?.full_name || user?.email}</p>
              {user?.tagline && <p className="text-xs text-zinc-500 truncate max-w-[200px]">{user.tagline}</p>}
            </div>
            <button data-testid="profile-settings-btn" onClick={() => setShowProfileSettings(true)} className="w-9 h-9 rounded-full bg-primary/20 text-primary flex items-center justify-center hover:bg-primary/30 transition-colors">
              <Settings size={16} />
            </button>
            {user?.is_admin && (
              <button data-testid="admin-dashboard-btn" onClick={() => navigate('/admin')} className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors" title="Admin Dashboard">
                <Crown size={16} />
              </button>
            )}
            <button data-testid="logout-btn" onClick={logout} className="w-9 h-9 rounded-full bg-white/5 text-zinc-400 flex items-center justify-center hover:bg-white/10 hover:text-white transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-4 text-sm font-bold uppercase tracking-wide border-b-2 transition-colors ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
                {tab.label}
              </button>
            ))}
          </div>
          <button data-testid="create-meetup-btn" onClick={() => setShowCreate(true)}
            className="px-5 py-2 text-sm font-bold uppercase tracking-wide text-black bg-primary rounded-md hover:bg-primary-hover transition-all glow-amber flex items-center gap-2">
            <Plus size={16} /> Create Meetup
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : currentMeetups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentMeetups.map(m => (
              <MeetupCard key={m.id} meetup={m} onJoin={handleJoin} onLeave={handleLeave} onViewDetails={setSelectedMeetup} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <Users size={48} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="font-heading text-2xl font-bold uppercase mb-2">
              {activeTab === 'my-meetups' ? 'No Meetups Yet' : 'No Meetups Found'}
            </h3>
            <p className="text-zinc-500 mb-6">
              {activeTab === 'my-meetups' ? 'Create or join a meetup to get started.' : 'Be the first to create a meetup!'}
            </p>
            <button onClick={() => setShowCreate(true)} className="px-8 py-3 bg-primary text-black font-bold uppercase rounded-md hover:bg-primary-hover transition-all glow-amber">
              Create a Meetup
            </button>
          </div>
        )}
      </main>

      {/* Modals */}
      {showCreate && <CreateMeetupForm onClose={() => setShowCreate(false)} onCreated={() => { fetchMeetups(); fetchMyMeetups(); }} />}
      {selectedMeetup && <MeetupDetailsModal meetup={selectedMeetup} onClose={() => setSelectedMeetup(null)} onJoin={handleJoin} onLeave={handleLeave} />}
      {showProfileSettings && <ProfileSettingsModal onClose={() => setShowProfileSettings(false)} />}
    </div>
  );
};

// ============ PROFILE SETTINGS ============

const ProfileSettingsModal = ({ onClose }) => {
  const [fullName, setFullName] = useState('');
  const [tagline, setTagline] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, fetchCurrentUser } = useAuth();

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setTagline(user.tagline || '');
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.put(`${API}/auth/profile`, { full_name: fullName, tagline });
      await fetchCurrentUser();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-surface border border-white/10 rounded-2xl max-w-md w-full shadow-2xl">
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold uppercase">Profile</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><X size={16} /></button>
          </div>
          {error && <div className="bg-accent/10 border border-accent/30 text-accent px-4 py-3 rounded-md mb-4 text-sm">{error}</div>}
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Full Name</label>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">Tagline ({140 - tagline.length})</label>
              <textarea value={tagline} onChange={(e) => setTagline(e.target.value)} maxLength={140} rows={2} placeholder="Philly sports fan since..."
                className="w-full px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={loading} className="flex-1 py-3 bg-primary text-black font-bold uppercase rounded-md hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber">
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button type="button" onClick={onClose} className="px-6 py-3 bg-white/5 text-white font-bold uppercase rounded-md hover:bg-white/10 transition-all">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============ APP ROUTING ============

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-bg flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.subscription_status !== 'active' && user.subscription_status !== 'promo') return <Navigate to="/season-pass" />;
  return children;
};

const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-bg flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  return user ? <Navigate to="/dashboard" /> : children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-bg flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (!user.is_admin) return <Navigate to="/dashboard" />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicOnlyRoute><LoginForm /></PublicOnlyRoute>} />
      <Route path="/register" element={<PublicOnlyRoute><RegisterForm /></PublicOnlyRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordForm />} />
      <Route path="/reset-password" element={<ResetPasswordForm />} />
      <Route path="/season-pass" element={<SeasonPassPage />} />
      <Route path="/season-pass/success" element={<SeasonPassSuccessPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/venues" element={<VenuesPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/meetups" element={<EventsPage />} />
      <Route path="/m/:shareId" element={<PublicMeetupPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
